import React, { useContext, useState, useMemo } from 'react';
import { FlatList, StyleSheet, View } from 'react-native';
import { Button, ListItem, Text, useTheme } from '@rneui/themed';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { FontAwesome } from '@expo/vector-icons';
import EnterNumberDialog from '../components/EnterNumberDialog';
import EditUnitDialog from '../components/EditUnitDialog';
import { useConsumption } from '../hooks/useConsumption';
import ConsumptionContext from '../consumption-context';

const LogScreen = ({
  addConsumption,
  addOrUpdateDailyComment,
  confirmZero,
}) => {
  const { consumption, dailyComments, total, userDoc } =
    useContext(ConsumptionContext);
  const { stdUnitDay, stdUnitWeek } = userDoc;
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const [addDaysDialogVisible, setAddDaysDialogVisible] = useState(false);
  const [editUnitDialogState, setEditUnitDialogState] = useState({
    visible: false,
    date: '0001-01-01',
  });
  const { insertEmptyDays, totalByUnitForDay } = useConsumption();

  const minDate = useMemo(() => {
    return _.min(Object.keys(total.daily));
  }, [total.daily]);

  const unitZeroHash = useMemo(
    (c) =>
      consumption
        .filter((e) => e.unitId === 0)
        .reduce(
          (acc, obj) => ((acc[obj.datetime.substring(0, 10)] = obj), acc),
          {}
        ),
    [consumption]
  );

  const fromTo = (numDays) => {
    if (numDays < 1) {
      return {};
    }
    const to = dayjs(minDate).add(-1, 'days').startOf('day');
    const from = to.add(-1 * (numDays - 1), 'days');
    return { from, to };
  };

  const renderItem = ({ item }) => {
    //console.log('---> item', item, dailyComments[item]);
    if (item.last) {
      return (
        <Button
          style={{ marginTop: 20 }}
          title="Backa startdatum"
          onPress={() => setAddDaysDialogVisible(true)}
        />
      );
    }

    const d = dayjs(item);
    const totalForDate = total.daily[item];
    const totalColor =
      totalForDate === 0
        ? unitZeroHash[item].confirmed
          ? 'green'
          : 'lightgrey'
        : theme.colors.primary;
    return (
      <ListItem
        bottomDivider
        containerStyle={styles.listItemContainerStyle}
        onPress={() =>
          setEditUnitDialogState({
            visible: true,
            date: d.format('YYYY-MM-DD'),
          })
        }
      >
        <ListItem.Content>
          <ListItem.Title>{_.capitalize(d.format('dddd'))}</ListItem.Title>
          <ListItem.Subtitle>{d.format('YYYY-MM-DD')}</ListItem.Subtitle>
        </ListItem.Content>
        <ListItem.Content right>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            {dailyComments?.find((c) => c.date === item)?.comment && (
              <FontAwesome
                style={{ marginRight: 5 }}
                name="pencil-square-o"
                size={20}
                color={theme.colors.primary}
              />
            )}

            <View style={[styles.totalView, { backgroundColor: totalColor }]}>
              <Text h2 h2Style={styles.totalText}>
                {totalForDate}
              </Text>
            </View>
          </View>
        </ListItem.Content>
      </ListItem>
    );
  };

  const listItems = Object.keys(total.daily);
  listItems.push({ last: true });
  return (
    <View style={{ flex: 1 }}>
      <FlatList
        style={{ paddingHorizontal: 20 }}
        data={listItems}
        renderItem={renderItem}
        keyExtractor={(item) => item}
      />
      <EditUnitDialog
        date={editUnitDialogState.date}
        visible={editUnitDialogState.visible}
        onOkPress={() => setEditUnitDialogState({ visible: false })}
        addConsumption={addConsumption}
        onConfirmZeroPress={() =>
          confirmZero(unitZeroHash[editUnitDialogState.date]?.id)
        }
        consumptionGoal={{ stdUnitDay, stdUnitWeek }}
        totalByUnitForDay={totalByUnitForDay}
        zeroConfirmed={unitZeroHash[editUnitDialogState.date]?.confirmed}
        dailyComment={
          dailyComments?.find((c) => c.date === editUnitDialogState.date)
            ?.comment
        }
        addOrUpdateDailyComment={(comment) =>
          addOrUpdateDailyComment(editUnitDialogState.date, comment)
        }
      />
      <EnterNumberDialog
        title={'Antal dagar att backa kalendern'}
        visible={addDaysDialogVisible}
        onOkPress={async (number) => {
          setAddDaysDialogVisible(false);
          await insertEmptyDays(fromTo(number));
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  listItemContainerStyle: { backgroundColor: 'light' },
  totalView: { padding: 3, borderRadius: 3 },
  totalText: { color: 'white' },
});

export default LogScreen;
